import { Eye, ThumbsUp } from 'lucide-react';

import { Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Trans } from '@lingui/react/macro';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, history } from 'store/store';
import useFabric from 'modules/overlay-canvas/hooks/use-fabric';
import UDText from 'ud-ui/components/text';
import { selectBoardsByTemplateId } from 'modules/template-editor/store/templates/selectors';
import { setActiveTemplate } from 'modules/template-editor/store/templates/templates';
import useDeleteModal from 'modules/template-editor/ui/hooks/modals/template/delete';
import useEditModal from 'modules/template-editor/ui/hooks/modals/template/edit';
import { ButtonWatch, ButtonWrapper, CanvasWrapper, TemplateItemWrapper } from './styles';
import { theme } from 'styles/theme/theme';

interface IProps {
  template: Template;
}

function TemplateItem(props: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { template } = props;
  const boards = useSelector(selectBoardsByTemplateId(template.id));
  const [isHover, setIsHover] = useState(false);
  const { manager, canvas } = useFabric('static', { id: template.id.toString(), data: { ...template.resolution } });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteModal();
  const { modal: editModal, open: openEditModal } = useEditModal();

  useEffect(() => {
    if (!manager) return;

    const resizing = () => {
      manager.setParentSize();
      manager.normalizedViewport();
    };

    const board = boards[0];
    if (!board) return;
    const scheme = { objects: board.objects, version: board.version };
    manager.loadLocal({ scheme: scheme, safeBackground: true });

    resizing();
    window.addEventListener('resize', resizing);
    return () => {
      window.removeEventListener('resize', resizing);
    };
  }, [boards, manager]);

  const onClick = useCallback(() => {
    dispatch(setActiveTemplate({ id: template.id }));
    history.push(Pages.TEMPLATE.CATALOG.builders.card(template.id));
  }, [dispatch, template.id]);

  const isAdmin = useIsAdmin();

  const onClickEdit = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      if (!isAdmin) return;
      dispatch(setActiveTemplate({ id: template.id }));
      openEditModal();
    },
    [dispatch, isAdmin, openEditModal, template.id],
  );

  const onDelete = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      if (!isAdmin) return;
      dispatch(setActiveTemplate({ id: template.id }));
      openDeleteModal();
    },
    [dispatch, isAdmin, openDeleteModal, template.id],
  );

  return (
    <TemplateItemWrapper>
      <CanvasWrapper onClick={onClick} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
        {canvas}
        <ButtonWrapper>
          <ButtonWatch onClick={onClick} isShow={isHover}>
            <Trans>Смотреть</Trans>
          </ButtonWatch>
          {isAdmin && (
            <>
              <ButtonWatch isShow={isHover} onClick={onClickEdit}>
                <Trans>Редактировать</Trans>
              </ButtonWatch>
              <ButtonWatch isShow={isHover} onClick={onDelete}>
                <Trans>Удалить</Trans>
              </ButtonWatch>
            </>
          )}
        </ButtonWrapper>
      </CanvasWrapper>
      <div className={'flex items-center justify-between'} style={{ marginTop: 8 }}>
        <UDText type={'caption-1'} style={{ color: theme.colors.SurfaceRaisinBlack50, fontWeight: 'bold' }}>
          {template.name}
        </UDText>
        <div className="flex-center" style={{ gap: 14, color: theme.colors.WhiteGray }}>
          <div className="flex-center" style={{ gap: 4 }}>
            <ThumbsUp size={12} />
            <UDText type={'tip'} style={{ color: 'inherit' }}>
              {template.likes?.length}
            </UDText>
          </div>
          <div className="flex-center" style={{ gap: 4 }}>
            <Eye size={12} />
            <UDText type={'tip'} style={{ color: 'inherit' }}>
              {template.views?.length}
            </UDText>
          </div>
        </div>
      </div>
      <UDText type={'tip'} style={{ color: theme.colors.WhiteGray, marginTop: 2 }}>
        {template.ownerId ?? 'ImGame'}
      </UDText>
      {deleteModal}
      {editModal}
    </TemplateItemWrapper>
  );
}

export default TemplateItem;
